// UI strings for Illan tie (2026-10-13, together/evening/): the couple session as one evening script of five
// blocks on a road, the week's Yhdessä card, the partner feedback and the hand-over. The Finnish follows the
// chosen prototype (docs/design/directions-2026-10-13/couple-prototypes.html, variant 3). Finnish (default)
// and English side by side, the same shape.
import type { EveningBlock } from '@valyrio/engine/pedagogy/evening';
import { defineArea } from '../area';

/** Names inflected as Finnish words (docs/FINNISH-STYLE.md "Nimien taivutus"): a consonant end takes i; vowel harmony by a, o, u. */
const vowelEnd = (name: string) => /[aeiouyäöå]$/i.test(name);
const back = (name: string) => /[aou]/i.test(name);
const stem = (name: string) => (vowelEnd(name) ? name : `${name}i`);
/** Roosan, Axelin */
const fiGen = (name: string) => `${stem(name)}n`;
/** Roosaa, Axelia */
const fiPart = (name: string) => `${stem(name)}${back(name) ? 'a' : 'ä'}`;
/** Roosalla, Axelilla */
const fiAde = (name: string) => `${stem(name)}${back(name) ? 'lla' : 'llä'}`;
/** Roosalle, Axelille */
const fiAll = (name: string) => `${stem(name)}lle`;

const minutes: Record<EveningBlock, string> = { warm: '3 min', teach: '8 min', scene: '10 min', talk: '10–15 min', end: '2 min' };

export const eveningFi = {
  title: 'Illan tie',
  stations: { warm: 'Lämmittely', teach: 'Opettaja ja oppilas', scene: 'Kohtaus', talk: 'Puhutaan', end: 'Lopuksi' } as Record<EveningBlock, string>,
  /** The road's labels under the stations (short enough for a fifth of a phone's width). */
  roadLabels: { warm: 'Lämmittely', teach: 'Opettaja ja oppilas', scene: 'Kohtaus', talk: 'Puhutaan', end: 'Lopuksi' } as Record<EveningBlock, string>,
  minutes,
  head: (station: string, min: string) => `${station} · ${min}`,
  // the week's card
  about: 'Noin 35 min',
  start: 'Aloita ilta',
  resume: (station: string) => `Jatka: ${station}`,
  later: (min: number) => `Jatketaan myöhemmin · noin ${min} min jäljellä`,
  doneBoth: 'Tehty · molemmat',
  doneOn: (date: string) => `Tehty · molemmat · ${date}`,
  doneByHand: (date: string) => `Merkitty tehdyksi · ${date}`,
  ready: 'Teimme jo tämän',
  undo: 'Peru',
  noWeek: 'Ilta alkaa, kun ensimmäinen oppitunti on avoinna.',
  // every block
  close: 'Jatketaan myöhemmin',
  skip: 'Ohita tämä',
  next: 'Seuraava',
  nextTo: (station: string) => `Seuraava: ${station}`,
  road: 'Illan tie',
  stationDone: (station: string) => `${station}: tehty`,
  stationSkipped: (station: string) => `${station}: ohitettu`,
  stationHere: (station: string) => `${station}: olette tässä`,
  you: 'Sinä',
  // Lämmittely
  starts: (name: string) => `${name} aloittaa`,
  youStart: 'Sinä aloitat',
  warmHow: 'Lukekaa viime viikon kohtaus, roolit vaihdettuina.',
  warmHowFirst: 'Lukekaa tämän viikon repliikit vuorotellen.',
  listenOnce: 'Kuuntele Petersonia kerran',
  lineCount: (n: number) => (n === 1 ? '1 repliikki' : `${n} repliikkiä`),
  warmEmpty: 'Viime viikolta ei ole repliikkejä. Siirtykää seuraavaan.',
  // the feedback card (once per person) and the feedback after a block
  guideHead: 'Ennen ensimmäistä palautetta',
  guide: ['Sano, mitä kuulit.', 'Osoita yksi sana.', 'Anna toisen yrittää uudelleen.'],
  guideOk: 'Selvä',
  guideHelp: 'Palauteohje',
  howWent: (name: string) => `Miten ${fiAde(name)} meni?`,
  good: 'Kuulosti hyvältä',
  oneWord: 'Yksi sana',
  again: 'Kokeile uudelleen',
  pickWord: 'Napauta yhtä sanaa',
  rightForm: 'Oikea muoto',
  partOf: (fi: string) => `osa lausetta: ${fi}`,
  listenModel: 'Kuuntele malli',
  peterson: 'Peterson',
  noModel: 'Tähän lauseeseen ei ole Petersonin äänitettä.',
  againNote: (name: string) => `${name} lukee repliikkinsä vielä kerran.`,
  feedbackFrom: (name: string) => `${fiGen(name)} palaute`,
  // Opettaja ja oppilas
  teachTurn: (teacher: string, student: string) => `${teacher} opettaa · ${student} vastaa`,
  teachTurnYou: (teacher: string) => `${teacher} opettaa · sinä vastaat`,
  teachHow: (student: string) => `Sano suomeksi. ${student} vastaa valyriaksi.`,
  grades: { ok: 'Oikein', almost: 'Melkein', no: 'Ei vielä' },
  retry: (student: string) => `Kuuntele malli ja anna ${fiGen(student)} yrittää uudelleen.`,
  listenTo: (teacher: string) => `Kuuntele ${fiPart(teacher)}`,
  answerAloud: 'Vastaa valyriaksi ääneen.',
  leads: (teacher: string) => `${teacher} vie eteenpäin`,
  swap: 'Vaihda rooleja',
  swapNote: (teacher: string) => `Vaihda rooleja: nyt ${teacher} opettaa.`,
  practiceTag: 'harjoituslause',
  wordTag: 'sana',
  ownWordTag: 'meidän sana',
  key: 'Vastaus',
  teachEmpty: 'Tämän viikon oppitunnilla ei ole vielä sanoja eikä lauseita, joita opettaja voisi kysyä. Siirtykää seuraavaan.',
  count: (i: number, n: number) => `${i}/${n}`,
  // Kohtaus
  lineOf: (name: string) => `${fiGen(name)} repliikki`,
  yourLine: 'Sinun repliikkisi',
  sceneSteps: ['1 Kuuntele', '2 Varjosta ×2', '3 Äänitä'],
  listen: 'Kuuntele',
  actor: 'Näyttelijän ääntämys',
  shadow: 'Varjosta ×2',
  shadowHow: 'Sano repliikki yhtä aikaa mallin kanssa, kaksi kertaa.',
  record: 'Äänitä',
  recordsOwn: (name: string) => `${name} äänittää omassa puhelimessaan.`,
  noLineModel: 'Tähän repliikkiin ei ole mallia. Lukekaa se ääneen.',
  nextShadow: 'Seuraava: Varjosta',
  nextRecord: 'Seuraava: Äänitä',
  nextLine: 'Seuraava repliikki',
  oursTitle: 'Kohtaus meidän äänillämme',
  playOurs: 'Kuuntele kohtaus meidän äänillämme',
  recorded: 'äänitetty',
  notRecorded: 'ei vielä äänitetty',
  oursScene: 'Oma lyhyt kohtaus Petersonin lauseista',
  sceneEmpty: 'Tälle viikolle ei ole kohtausta. Siirtykää seuraavaan.',
  allScenes: 'Kaikki kohtaukset',
  // Puhutaan
  talkStep: (n: number) => `Puhumisen askel ${n}/5`,
  talkEmpty: 'Tälle askeleelle ei ole vielä tämän viikon vuoroja. Siirtykää seuraavaan.',
  talkLoading: 'Puhumisen askel latautuu.',
  // Lopuksi
  halloween: (label: string) => `Halloween-lause · ${label}`,
  nextWeek: 'Ensi viikko',
  sameAsLast: 'Sama kuin viime viikolla',
  change: 'Muuta',
  noPlan: 'Ei vielä suunnitelmaa',
  makePlan: 'Tee suunnitelma',
  feel: 'Miltä ilta tuntui?',
  feelFor: (name: string) => `${name}: miltä ilta tuntui?`,
  feelGood: 'Hyvältä',
  feelOk: 'Ihan hyvältä',
  feelBad: 'Ei oikein hyvältä',
  finish: 'Valmis',
  // the end
  nextLesson: (n: number) => `Ensi viikko: oppitunti ${n}. Kohtaus vaihtuu, roolit vaihtuvat.`,
  back: 'Takaisin viikkoon',
  handTo: (name: string) => `Anna puhelin ${fiAll(name)}`,
};

export type EveningMessages = typeof eveningFi;

export const eveningEn: EveningMessages = {
  title: 'The evening road',
  stations: { warm: 'Warm-up', teach: 'Teacher and student', scene: 'Scene', talk: 'Let’s talk', end: 'To finish' },
  roadLabels: { warm: 'Warm-up', teach: 'Teacher and student', scene: 'Scene', talk: 'Let’s talk', end: 'To finish' },
  minutes,
  head: (station, min) => `${station} · ${min}`,
  about: 'About 35 min',
  start: 'Start the evening',
  resume: (station) => `Continue: ${station}`,
  later: (min) => `Continue later · about ${min} min left`,
  doneBoth: 'Done · both',
  doneOn: (date) => `Done · both · ${date}`,
  doneByHand: (date) => `Marked done · ${date}`,
  ready: 'We already did this',
  undo: 'Undo',
  noWeek: 'The evening starts once the first lesson is open.',
  close: 'Continue later',
  skip: 'Skip this',
  next: 'Next',
  nextTo: (station) => `Next: ${station}`,
  road: 'The evening road',
  stationDone: (station) => `${station}: done`,
  stationSkipped: (station) => `${station}: skipped`,
  stationHere: (station) => `${station}: you are here`,
  you: 'You',
  starts: (name) => `${name} starts`,
  youStart: 'You start',
  warmHow: 'Read last week’s scene with the roles swapped.',
  warmHowFirst: 'Read this week’s lines in turns.',
  listenOnce: 'Listen to Peterson once',
  lineCount: (n) => (n === 1 ? '1 line' : `${n} lines`),
  warmEmpty: 'There are no lines from last week. Go on to the next part.',
  guideHead: 'Before the first feedback',
  guide: ['Say what you heard.', 'Point to one word.', 'Let the other try again.'],
  guideOk: 'Got it',
  guideHelp: 'How to give feedback',
  howWent: (name) => `How did ${name} do?`,
  good: 'Sounded good',
  oneWord: 'One word',
  again: 'Try again',
  pickWord: 'Tap one word',
  rightForm: 'The right form',
  partOf: (fi) => `part of: ${fi}`,
  listenModel: 'Listen to the model',
  peterson: 'Peterson',
  noModel: 'Peterson has not recorded this line.',
  againNote: (name) => `${name} reads the line once more.`,
  feedbackFrom: (name) => `${name}’s feedback`,
  teachTurn: (teacher, student) => `${teacher} teaches · ${student} answers`,
  teachTurnYou: (teacher) => `${teacher} teaches · you answer`,
  teachHow: (student) => `Say it in Finnish. ${student} answers in High Valyrian.`,
  grades: { ok: 'Right', almost: 'Almost', no: 'Not yet' },
  retry: (student) => `Listen to the model and let ${student} try again.`,
  listenTo: (teacher) => `Listen to ${teacher}`,
  answerAloud: 'Answer aloud in High Valyrian.',
  leads: (teacher) => `${teacher} moves on`,
  swap: 'Swap roles',
  swapNote: (teacher) => `Swap roles: now ${teacher} teaches.`,
  practiceTag: 'practice sentence',
  wordTag: 'word',
  ownWordTag: 'our word',
  key: 'Answer',
  teachEmpty: 'This week’s lesson has no words or lines for the teacher’s key yet. Go on to the next part.',
  count: (i, n) => `${i}/${n}`,
  lineOf: (name) => `${name}’s line`,
  yourLine: 'Your line',
  sceneSteps: ['1 Listen', '2 Shadow ×2', '3 Record'],
  listen: 'Listen',
  actor: 'The actor’s pronunciation',
  shadow: 'Shadow ×2',
  shadowHow: 'Say the line together with the model, twice.',
  record: 'Record',
  recordsOwn: (name) => `${name} records on their own phone.`,
  noLineModel: 'There is no model for this line. Read it aloud.',
  nextShadow: 'Next: Shadow',
  nextRecord: 'Next: Record',
  nextLine: 'Next line',
  oursTitle: 'The scene in our voices',
  playOurs: 'Listen to the scene in our voices',
  recorded: 'recorded',
  notRecorded: 'not recorded yet',
  oursScene: 'A short scene of ours from Peterson’s lines',
  sceneEmpty: 'There is no scene this week. Go on to the next part.',
  allScenes: 'All scenes',
  talkStep: (n) => `Speaking step ${n}/5`,
  talkEmpty: 'This step has no turns this week yet. Go on to the next part.',
  talkLoading: 'The speaking step is loading.',
  halloween: (label) => `Halloween line · ${label}`,
  nextWeek: 'Next week',
  sameAsLast: 'Same as last week',
  change: 'Change',
  noPlan: 'No plan yet',
  makePlan: 'Make a plan',
  feel: 'How did the evening feel?',
  feelFor: (name) => `${name}: how did the evening feel?`,
  feelGood: 'Good',
  feelOk: 'All right',
  feelBad: 'Not so good',
  finish: 'Done',
  nextLesson: (n) => `Next week: lesson ${n}. A new scene, the roles swap.`,
  back: 'Back to the week',
  handTo: (name) => `Hand the phone to ${name}`,
};

// Messages.evening (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('evening', eveningFi, eveningEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof eveningFi> {}
}
